import { useCallback, useContext, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { SuggestionProduct } from '@atlassian/navigation-bff-data-provider';

import {
	ANALYTICS_CHANNEL,
	ANALYTICS_SOURCE,
	ANALYTICS_TAG,
	NAV_CONTAINER,
	NAV_LAYER,
	NAV_VERSION,
} from '../constants';
import type { SourceProduct } from '../types';
import { MenuControlContext } from '../utils';

export interface MenuContentViewedAttributes {
	crossAppSuggestionsEnabled: boolean;
	nativeSections: Array<{
		itemAtis: string[];
		itemCount: number;
		sectionHeading?: string;
		sectionIndex: number;
		sectionType: string;
	}>;
	totalNativeItems: number;
	totalNativeSections: number;
}

export interface MenuItemClickedAttributes {
	ati: string;
	cloudId?: string;
	sourceProduct?: SourceProduct;
	suggestionProduct?: SuggestionProduct;
}

export interface SuggestionMenuViewedAttributes {
	suggestedItems: string[];
	suggestionsCount: number;
}
export interface CreateButtonClickedAttributes {
	itemState: 'expanded' | 'collapsed';
}

export interface NewMenuItemClickedAttributes {
	ati: string;
	groupItem: string;
	groupItemProduct: SuggestionProduct | SourceProduct;
	groupItemsCount: number;
	groupItemsIndex: number;
	isRightClick: boolean;
	isSuggestion: boolean;
	menuItemIndex: number;
	menuItemsCount: number;
}

export interface UniversalCreateAnalyticsEvents {
	// Create button click event
	fireCreateButtonClicked: (attributes: CreateButtonClickedAttributes) => void;

	// Menu content events
	fireMenuContentViewed: (attributes: MenuContentViewedAttributes) => void;

	// Click menu item events
	fireMenuItemClicked: (attributes: NewMenuItemClickedAttributes) => void;

	// Menu item events
	fireNativeMenuItemClicked: (attributes: MenuItemClickedAttributes) => void;

	fireRemoteMenuItemClicked: (attributes: MenuItemClickedAttributes) => void;
	// Suggestion menu events
	fireSuggestionMenuViewed: (attributes: SuggestionMenuViewedAttributes) => void;

	// Trigger button events
	fireTriggerButtonClicked: () => void;
}

/**
 * Custom hook for Universal Create analytics events
 * Provides type-safe, consistent analytics event firing across all UC components
 */
export function useUniversalCreateAnalytics(): UniversalCreateAnalyticsEvents {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { product, subProduct } = useContext(MenuControlContext);

	// Provide fallback for product if undefined
	const safeProduct = product || 'unknown';

	const baseAttributes = useMemo(
		() => ({
			navLayer: NAV_LAYER,
			navContainer: NAV_CONTAINER,
			navVersion: NAV_VERSION,
			product: safeProduct,
			subProduct,
		}),
		[safeProduct, subProduct],
	);

	const topLevelAttributes = useMemo(
		() => ({
			actionSubject: 'universalCreate',
			eventType: 'ui',
			source: ANALYTICS_SOURCE,
			tags: [ANALYTICS_TAG],
		}),
		[],
	);

	const fireCreateButtonClicked = useCallback(
		(attributes: CreateButtonClickedAttributes) => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubjectId: 'universalCreateButton',
				...topLevelAttributes,
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	const fireMenuItemClicked = useCallback(
		(attributes: NewMenuItemClickedAttributes) => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubjectId: 'universalCreateMenuItem',
				...topLevelAttributes,
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	const nativeSubject = 'UCMenuNativeContent';

	const fireMenuContentViewed = useCallback(
		(attributes: MenuContentViewedAttributes) => {
			createAnalyticsEvent({
				action: 'viewed',
				actionSubject: nativeSubject,
				name: nativeSubject,
				eventType: 'screen',
				tags: [ANALYTICS_TAG],
				attributes: {
					...attributes,
					product: safeProduct,
					subProduct,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, safeProduct, subProduct],
	);

	const suggestionsSubject = 'UCMenuSuggestions';

	const fireSuggestionMenuViewed = useCallback(
		(attributes: SuggestionMenuViewedAttributes) => {
			createAnalyticsEvent({
				action: 'viewed',
				actionSubject: suggestionsSubject,
				name: suggestionsSubject,
				eventType: 'screen',
				tags: [ANALYTICS_TAG],
				attributes: {
					...attributes,
					product: safeProduct,
					subProduct,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, subProduct, safeProduct],
	);

	const nativeItemSubject = 'UCMenuNativeItem';

	const fireNativeMenuItemClicked = useCallback(
		(attributes: MenuItemClickedAttributes) => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: nativeItemSubject,
				eventType: 'ui',
				tags: [ANALYTICS_TAG],
				attributes: {
					...attributes,
					product: safeProduct,
					subProduct,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, safeProduct, subProduct],
	);

	const remoteItemSubject = 'RemoteUCMenuItem';

	const fireRemoteMenuItemClicked = useCallback(
		(attributes: MenuItemClickedAttributes) => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: remoteItemSubject,
				eventType: 'ui',
				tags: [ANALYTICS_TAG],
				attributes: {
					...attributes,
					product: safeProduct,
					subProduct,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, safeProduct, subProduct],
	);

	const fireTriggerButtonClicked = useCallback(() => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'universalCreateTriggerButton',
			eventType: 'ui',
			tags: [ANALYTICS_TAG],
			attributes: {
				product: safeProduct,
				subProduct,
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, safeProduct, subProduct]);

	return {
		fireMenuContentViewed,
		fireSuggestionMenuViewed,
		fireNativeMenuItemClicked,
		fireRemoteMenuItemClicked,
		fireTriggerButtonClicked,
		fireCreateButtonClicked,
		fireMenuItemClicked,
	};
}
